import { useQuery } from '@tanstack/react-query';
import { Activity, ArrowLeft, CalendarCheck, CalendarDays, CheckCircle2, Clock, CreditCard, Dog, LogOut, Maximize2, Menu, Minimize2, PanelLeftClose, PanelLeftOpen, Scissors, Sparkles, Tags, Ticket, Truck, UserCog, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { AppErrorBoundary } from '../components/ui/app-error-boundary';
import { useAuth } from '../features/auth/AuthProvider';
import { cn } from '../shared/cn';
import { copy, labelForStaffRole } from '../shared/i18n';
import { api } from '../shared/api';
import { canAccessModule, defaultModulePermissions, normalizeModulePermissions, type ModuleId, type StaffRole } from '../shared/modulePermissions';

const navItems: Array<{ to: string; label: string; icon: typeof Activity; moduleId: ModuleId }> = [
  { to: '/admin/dashboard', label: copy.app.nav.dashboard, icon: Activity, moduleId: 'dashboard' },
  { to: '/admin/calendar', label: 'Calendario', icon: CalendarDays, moduleId: 'calendar' },
  { to: '/admin/grooming', label: 'Grooming / estetica', icon: CalendarCheck, moduleId: 'grooming' },
  { to: '/admin/transportation', label: 'Transporte', icon: Truck, moduleId: 'grooming' },
  { to: '/admin/client-dogs', label: 'Clientes + perros', icon: Dog, moduleId: 'client-dogs' },
  { to: '/admin/stays', label: copy.app.nav.stays, icon: CalendarDays, moduleId: 'stays' },
  { to: '/admin/daycares', label: 'Guarderia', icon: Clock, moduleId: 'daycares' },
  { to: '/admin/memberships', label: 'Membresias', icon: Ticket, moduleId: 'memberships' },
  { to: '/admin/services', label: copy.app.nav.services, icon: Scissors, moduleId: 'services' },
  { to: '/admin/prices', label: 'Precios', icon: Tags, moduleId: 'prices' },
  { to: '/admin/payments', label: 'Pagos', icon: CreditCard, moduleId: 'payments' },
  { to: '/admin/users', label: copy.app.nav.users, icon: UserCog, moduleId: 'users' },
];

const navGroups = [
  ['/admin/dashboard', '/admin/calendar'],
  ['/admin/client-dogs'],
  ['/admin/grooming', '/admin/transportation', '/admin/stays', '/admin/daycares', '/admin/memberships'],
  ['/admin/services', '/admin/prices'],
  ['/admin/payments'],
  ['/admin/users'],
];

const sidebarPreferenceKey = 'hotle.admin.sidebar-collapsed';
const contentExpandedPreferenceKey = 'hotle.admin.content-expanded';
const whatsNewVersion = '2026-09-19-payments-categories';
const whatsNewSeenVersionKey = 'hotle.admin.whats-new.seen-version';

export function AppLayout() {
  return (
    <AppErrorBoundary area="shell" variant="page">
      <AppLayoutContent />
    </AppErrorBoundary>
  );
}

function AppLayoutContent() {
  const [open, setOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarPreference);
  const [contentExpanded, setContentExpanded] = useState(readContentExpandedPreference);
  const { user, signOut } = useAuth();
  const permissionsQuery = useQuery({ queryKey: ['staff-role-permissions'], queryFn: api.staffRolePermissions.get, enabled: Boolean(user) });
  const navigate = useNavigate();
  const location = useLocation();
  const displayName = user?.name ?? user?.email ?? copy.app.signedIn;
  const userRoles = (user?.groups ?? []) as StaffRole[];
  const permissions = normalizeModulePermissions(permissionsQuery.data?.permissions ?? defaultModulePermissions());
  const visibleItems = navItems.filter((item) => canAccessModule(userRoles, item.moduleId, permissions));

  useEffect(() => {
    try {
      window.localStorage.setItem(sidebarPreferenceKey, String(sidebarCollapsed));
    } catch {
      // Keep the control usable when storage is unavailable.
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    try {
      window.localStorage.setItem(contentExpandedPreferenceKey, String(contentExpanded));
    } catch {
      // Keep the control usable when storage is unavailable.
    }
  }, [contentExpanded]);

  return (
    <div className="min-h-screen bg-[#f8faf6] text-[#223126]">
      <AppErrorBoundary area="navigation" variant="sidebar" resetKey={location.pathname}>
        <aside className={cn('fixed inset-y-0 left-0 z-30 w-64 border-r border-[#d9dfd1] bg-white transition-[transform,width] lg:translate-x-0', sidebarCollapsed ? 'lg:w-20' : 'lg:w-64', open ? 'translate-x-0' : '-translate-x-full')}>
          <div className={cn('flex h-16 items-center gap-3 border-b border-[#d9dfd1] px-4', sidebarCollapsed && 'lg:justify-center')}>
            <img src="/happy-bark-logo.webp" alt="Happy Bark" className={cn('h-10 w-10 rounded-full object-cover ring-1 ring-[#d9dfd1]', sidebarCollapsed && 'lg:hidden')} />
            <div className={cn(sidebarCollapsed && 'lg:hidden')}>
              <p className="text-sm font-semibold leading-4 text-[#223126]">Happy Bark</p>
              <p className="text-xs text-[#687461]">{copy.app.staffOperations}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              className={cn('ml-auto hidden h-9 w-9 px-0 lg:inline-flex', sidebarCollapsed && 'lg:ml-0')}
              title={sidebarCollapsed ? 'Expandir panel' : 'Contraer panel'}
              aria-label={sidebarCollapsed ? 'Expandir panel lateral' : 'Contraer panel lateral'}
              onClick={() => setSidebarCollapsed((current) => !current)}
            >
              {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" aria-hidden="true" /> : <PanelLeftClose className="h-5 w-5" aria-hidden="true" />}
            </Button>
          </div>

          <nav className="space-y-3 p-3">
            {navGroups.map((group) => {
              const groupItems = group.map((to) => visibleItems.find((item) => item.to === to)).filter((item): item is (typeof navItems)[number] => Boolean(item));
              if (groupItems.length === 0) return null;
              return (
                <div key={group.join('|')} className="space-y-1 border-t border-[#eef1e9] pt-3 first:border-t-0 first:pt-0">
                  {groupItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setOpen(false)}
                        title={sidebarCollapsed ? item.label : undefined}
                        className={({ isActive }) => cn('flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium text-[#5f6b58] transition', sidebarCollapsed && 'lg:justify-center lg:px-0', isActive ? 'bg-[#eef3ea] text-[#2f3d2e]' : 'hover:bg-[#f3e5df] hover:text-[#2f3d2e]')}
                      >
                        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        <span className={cn(sidebarCollapsed && 'lg:sr-only')}>{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </aside>
      </AppErrorBoundary>

      {open ? <button aria-label={copy.app.closeNavigation} className="fixed inset-0 z-20 bg-[#223126]/30 lg:hidden" onClick={() => setOpen(false)} /> : null}

      <div className={cn('min-w-0 transition-[padding]', sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64')}>
        <AppErrorBoundary area="header" variant="header" resetKey={location.pathname}>
          <header className="sticky top-0 z-10 flex min-w-0 min-h-16 items-center gap-3 border-b border-[#d9dfd1] bg-white/95 px-4 backdrop-blur">
          <Button variant="ghost" className="h-9 w-9 px-0 lg:hidden" onClick={() => setOpen(true)} aria-label={copy.app.openNavigation}>
            <Menu className="h-5 w-5" aria-hidden="true" />
          </Button>
          <Button variant="ghost" className="h-9 w-9 px-0" onClick={() => location.pathname === '/admin/dashboard' ? navigate('/') : navigate(-1)} aria-label="Volver">
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </Button>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#223126]">{copy.app.operationsTitle}</p>
            <p className="text-xs text-[#687461]">{copy.app.staffConsole}</p>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              className="h-9 w-9 px-0"
              title={contentExpanded ? 'Usar ancho normal' : 'Expandir contenido'}
              aria-label={contentExpanded ? 'Usar ancho normal' : 'Expandir contenido'}
              onClick={() => setContentExpanded((current) => !current)}
            >
              {contentExpanded ? <Minimize2 className="h-4 w-4" aria-hidden="true" /> : <Maximize2 className="h-4 w-4" aria-hidden="true" />}
            </Button>
            <div className="hidden min-w-0 text-right sm:block">
              <p className="truncate text-xs font-medium text-[#4a5845]">{displayName}</p>
              {user?.groups.length ? <p className="truncate text-xs text-[#687461]">{user.groups.map(labelForStaffRole).join(', ')}</p> : null}
            </div>
            <Button variant="secondary" onClick={signOut} aria-label={copy.app.signOut}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">{copy.app.signOut}</span>
            </Button>
          </div>
          </header>
        </AppErrorBoundary>

        <main className={cn('mx-auto min-w-0 w-full px-4 py-5 sm:px-6 lg:px-8', contentExpanded ? 'max-w-none' : 'max-w-7xl')}>
          <AppErrorBoundary area="content" variant="content" resetKey={location.pathname}>
            <Outlet />
          </AppErrorBoundary>
        </main>
      </div>
      <WhatsNewDialog />
    </div>
  );
}

function WhatsNewDialog() {
  const [open, setOpen] = useState(() => readWhatsNewSeenVersion() !== whatsNewVersion);
  const [hideAgain, setHideAgain] = useState(true);

  const close = () => {
    if (hideAgain) writeWhatsNewSeenVersion(whatsNewVersion);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[#223126]/35 p-4" role="dialog" aria-modal="true" aria-labelledby="whatsNewTitle">
      <div className="w-full max-w-md rounded-md border border-[#d6ded0] bg-white shadow-xl">
        <div className="flex items-start gap-3 border-b border-[#eef1e9] p-4">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#eef3ea] text-[#2f6b38]">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p id="whatsNewTitle" className="text-base font-semibold text-[#223126]">Novedades</p>
            <p className="mt-1 text-sm text-[#687461]">Nuevos servicios y mejoras en los cobros.</p>
          </div>
          <Button type="button" variant="ghost" className="h-8 w-8 px-0" aria-label="Cerrar novedades" onClick={close}>
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="grid gap-3 p-4">
          <div className="flex gap-2 text-sm text-[#334033]">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2f6b38]" aria-hidden="true" />
            <p>Veterinaria y Premios ya están disponibles para crear servicios y precios.</p>
          </div>
          <div className="flex gap-2 text-sm text-[#334033]">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2f6b38]" aria-hidden="true" />
            <p>Usa Otro para cobrar un precio y cantidad personalizados, con una descripción opcional. Los demás conceptos muestran su descripción automáticamente.</p>
          </div>
          <p className="text-sm text-[#334033]">Corregimos la carga de registros y saldos para consultar y registrar pagos más allá de los primeros 100.</p>
          <label className="mt-1 flex items-center gap-2 text-sm font-medium text-[#4f5d49]">
            <input type="checkbox" checked={hideAgain} onChange={(event) => setHideAgain(event.target.checked)} />
            No volver a mostrar estas novedades.
          </label>
          <div className="flex justify-end">
            <Button type="button" onClick={close}>Entendido</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function readSidebarPreference() {
  try {
    return window.localStorage.getItem(sidebarPreferenceKey) === 'true';
  } catch {
    return false;
  }
}

function readContentExpandedPreference() {
  try {
    return window.localStorage.getItem(contentExpandedPreferenceKey) === 'true';
  } catch {
    return false;
  }
}

function readWhatsNewSeenVersion() {
  try {
    return window.localStorage.getItem(whatsNewSeenVersionKey);
  } catch {
    return whatsNewVersion;
  }
}

function writeWhatsNewSeenVersion(version: string) {
  try {
    window.localStorage.setItem(whatsNewSeenVersionKey, version);
  } catch {
    // The dialog can still be dismissed for the current session.
  }
}
